import React, { useState } from "react";

import {
  Alert,
  Button,
  Card,
  Col,
  Dropdown,
  Menu,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tooltip,
} from "antd";
import { CaretDownOutlined } from "@ant-design/icons";

import { Area } from "@ant-design/charts";

import { chain, orderBy } from "lodash";

import Moment from "moment";
import { extendMoment } from "moment-range";

import { getPipeSchedule, convertInchesToCm } from "../../libs/utils";

const { TabPane } = Tabs;
const moment = extendMoment(Moment);

const filterData = [
  {
    key: 1,
    title: "Last 24 hours",
    value: 1,
    date: moment().subtract(1, "days"),
  },
  {
    key: 2,
    title: "Last 7 days",
    value: 7,
    date: moment().subtract(7, "days"),
  },
  {
    key: 3,
    title: "Last 30 days",
    value: 30,
    date: moment().subtract(30, "days"),
  },
];

function RobotAlarmStatistics(props) {
  const { data = [], weldRun = [], isCustomer } = props;
  const [dropdownKey, setDropdownKey] = useState(2);
  const [activeKey, setActiveKey] = useState("key2");
  const [showCleared, setShowCleared] = useState(false);

  // config for Trends Graph
  const config = {
    xField: "date",
    yField: "value",
    smooth: true,
    height: 60,
    width: 300,
  };

  const range = moment.rangeFromInterval("day", -30, moment());

  const filterDataBy = (type, from) =>
    Array.from(range.by("day"))
      .map((day) => {
        if (day.isAfter(from)) {
          return {
            date: day.format("YYYY-MM-DD"),
            value: data.filter(
              (item) =>
                day.format("YYYY-MM-DD") ===
                  moment(item.received_at).format("YYYY-MM-DD") &&
                item.type === type
            ).length,
            type: type,
          };
        }
      })
      .filter((item) => item);

  const statsData = {
    warning: data.filter(
      (item) =>
        moment(item.received_at).isSameOrAfter(
          filterData
            .find(({ key }) => key === dropdownKey)
            .date.format("YYYY-MM-DD")
        ) && item.type === "warning"
    ),
    error: data.filter(
      (item) =>
        moment(item.received_at).isSameOrAfter(
          filterData
            .find(({ key }) => key === dropdownKey)
            .date.format("YYYY-MM-DD")
        ) && item.type === "error"
    ),
    crash: data.filter(
      (item) =>
        moment(item.received_at).isSameOrAfter(
          filterData
            .find(({ key }) => key === dropdownKey)
            .date.format("YYYY-MM-DD")
        ) && item.type === "crash"
    ),
  };

  const trendsData = [
    ...filterDataBy(
      "warning",
      filterData
        .find(({ key }) => key === dropdownKey)
        .date.format("YYYY-MM-DD")
    ),
    ...filterDataBy(
      "error",
      filterData
        .find(({ key }) => key === dropdownKey)
        .date.format("YYYY-MM-DD")
    ),
    ...filterDataBy(
      "crash",
      filterData
        .find(({ key }) => key === dropdownKey)
        .date.format("YYYY-MM-DD")
    ),
  ];

  const columns = [
    {
      title: "Type",
      width: "5%",
      // dataIndex: "unit",
      filters: [
        { text: "Error", value: "error" },
        { text: "Warning", value: "warning" },
        { text: "Crash", value: "crash" },
      ],
      onFilter: (value, record) => record.type.indexOf(value) === 0,

      render: (text, item, index) => {
        return {
          children: (
            <Tooltip placement="topLeft" title={item.detail}>
              <Alert
                className="AlarmStatistics_Alert"
                style={{ margin: "-5px auto" }}
                type={item.type === "warning" ? "warning" : "error"}
                showIcon
                message={
                  <span>
                    <span>{item.message}</span> <i>{item.received_at}</i>
                  </span>
                }
              ></Alert>
            </Tooltip>
          ),
          props: { colSpan: 3 },
        };
      },
    },
    {
      title: "Message",
      sorter: {
        compare: (a, b) => {
          const aString = a.message.toLowerCase();
          const bString = b.message.toLowerCase();

          if (aString > bString) {
            return 1;
          }
          if (aString < bString) {
            return -1;
          }
          return 0;
        },
      },
      // dataIndex: "message",
    },
    {
      title: "Time",
      sorter: {
        compare: (a, b) => {
          const aDate = new Date(a.received_at);
          const bDate = new Date(b.received_at);

          if (aDate > bDate) {
            return 1;
          }
          if (aDate < bDate) {
            return -1;
          }
          return 0;
        },
        multiple: 1,
      },
      // dataIndex: "received_at",
    },
  ];

  const isMetric =
    weldRun && weldRun[0] && weldRun[0].units_system === "metric";

  const weldRunFilters = (key) => {
    return weldRun
      .filter((item) =>
        moment(item.arc_on_utc).isSameOrAfter(
          filterData
            .find(({ key }) => key === dropdownKey)
            .date.format("YYYY-MM-DD")
        )
      )
      .map((item) => item[key])
      .filter((value, index, self) => self.indexOf(value) === index)
      .map((item) => {
        return { text: item, value: item };
      });
  };

  const weldRunColumns = [
    { title: "" },
    {
      title: "Date",
      render: (_, item) => {
        if (item.children) {
          return <span>{moment(item.arc_off_utc).format("YYYY-MM-DD")}</span>;
        } else {
          return <span>{moment(item.arc_on_utc).format("YYYY-MM-DD")}</span>;
        }
      },
    },
    {
      title: "Order",
      dataIndex: "work_order_ref",
      filters: weldRunFilters("work_order_ref"),
      onFilter: (value, record) => record.work_order_ref.indexOf(value) === 0,
    },
    {
      title: "Spool",
      dataIndex: "spool_ref",
      filters: weldRunFilters("spool_ref"),
      onFilter: (value, record) => record.spool_ref.indexOf(value) === 0,
    },
    {
      title: "Welder ID",
      dataIndex: "welder_id_ref",
      filters: weldRunFilters("welder_id_ref"),
      onFilter: (value, record) => record.welder_id_ref.indexOf(value) === 0,
    },
    {
      title: "Run",
      dataIndex: "run_number", // should have count
    },
    {
      title: "Start Time",
      render: (_, item) => {
        return <span>{moment(item.arc_on_utc).format("HH:mm:ss")}</span>;
      },
    },
    {
      title: "Arc Off Time",
      render: (_, item) => {
        if (item.duration) {
          return (
            <span>
              {Number(item.duration.hours()) * 60 +
                Number(item.duration.minutes()) +
                "m "}
              {item.duration.seconds() + "s"}
            </span>
          );
        } else {
          return <span>{null}</span>;
        }
      },
    },
    {
      title: "Weld Time",
      render: (_, item) => {
        return (
          <span>
            {Number(moment.duration({ ...item.weld_time }).hours()) * 60 +
              Number(moment.duration({ ...item.weld_time }).minutes()) +
              "m "}
            {moment.duration({ ...item.weld_time }).seconds() + "s"}
          </span>
        );
      },
    },
    {
      title: "Pipe Size",
      filters: weldRun
        .map((item) => {
          return `${
            isMetric
              ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
              : item.pipe_size_inches
          }${isMetric ? "cm" : "in"}`;
        })
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((item) => {
          return { text: item, value: item };
        }),
      onFilter: (value, record) => {
        const unit =
          !record.units_system || record.units_system === "imperial"
            ? "in"
            : "cm";
        return `${record.pipe_size_inches}${unit}` === value;
      },
      render: (_, item) => {
        const unit =
          !item.units_system || item.units_system === "imperial" ? "in" : "cm";
        return <span>{`${item.pipe_size_inches}${unit}`}</span>;
      },
    },
    {
      title: "Pipe Sched",
      filters: weldRun
        .map((item) => {
          return item.pipe_schedule;
        })
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((item) => {
          return { text: item, value: item };
        }),
      onFilter: (value, record) => {
        return record.pipe_schedule === value;
      },
      render: (_, item) => {
        return <span>{getPipeSchedule(item.pipe_schedule)}</span>;
      },
    },
    {
      title: "FDI",
      align: "center",
      render: (_, item) => {
        if (
          Number(moment.duration({ ...item.weld_time }).asMinutes()) >=
          Number(moment.duration({ minutes: 1 }).asMinutes())
        ) {
          return <span>{item.weld_inches}</span>;
        } else {
          return <span></span>;
        }
      },
    },
    {
      title: "Operator",
      dataIndex: "operator_name",
      filters: weldRunFilters("operator_name"),
      onFilter: (value, record) => {
        if (record.children && record.children.length > 0) {
          console.log("?");
          return record.children
            .map((child) => child.operator_name.indexOf(value) === 0)
            .filter((c) => c);
        } else {
          console.log("!");
          return (
            record.operator_name && record.operator_name.indexOf(value) === 0
          );
        }
      },
    },
    {
      title: "Procedure",
      dataIndex: "procedure_name",
      filters: weldRunFilters("procedure_name"),
      onFilter: (value, record) => record.procedure_name.indexOf(value) === 0,
    },
  ];

  const pagination = { hideOnSinglePage: true };

  const groupedWeldRun = chain(weldRun)
    .map((item) => {
      const idx = weldRun.findIndex((i) => i === item);
      if (
        idx < weldRun.length - 1 &&
        moment(weldRun[idx + 1].arc_on_utc, "YYYY-MM-DD").diff(
          moment(item.arc_on_utc, "YYYY-MM-DD"),
          "days"
        ) === 0
      ) {
        const duration = moment.duration(
          moment(item.arc_on_utc).diff(moment(weldRun[idx + 1].arc_off_utc))
        );

        return {
          ...item,
          duration,
          pipe_size_inches: isMetric
            ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
            : item.pipe_size_inches,
          weld_inches: isMetric
            ? convertInchesToCm(item.weld_inches).toFixed(0)
            : item.weld_inches,
        };
      } else {
        return {
          ...item,
          pipe_size_inches: isMetric
            ? convertInchesToCm(item.pipe_size_inches).toFixed(0)
            : item.pipe_size_inches,
          weld_inches: isMetric
            ? convertInchesToCm(item.weld_inches).toFixed(0)
            : item.weld_inches,
        };
      }
    })
    .groupBy((item) => {
      return item.work_order_ref + " " + item.spool_ref;
    })
    .entries()
    .map(([work_order_spool, weld]) => {
      const children = orderBy(
        [...weld],
        [
          (i) => {
            return i.run_number;
          },
        ],
        ["desc"]
      );

      const childrenSize = children.length;
      const first = children[0];
      const last = children[childrenSize - 1];

      return {
        key: work_order_spool,
        children,
        arc_on_utc: last.arc_on_utc,
        arc_off_utc: first.arc_off_utc,
        work_order_ref: last.work_order_ref,
        spool_ref: last.spool_ref,

        units_system: last.units_system,
        pipe_size_inches: last.pipe_size_inches,
        pipe_schedule: last.pipe_schedule,

        // sum of arc off time (setup time) on grouped weld
        duration: children.slice(1).reduce((prev, curr) => {
          return prev.add(curr.duration);
        }, moment.duration(first.duration)),

        // sum of weld time on grouped weld
        weld_time: children.slice(1).reduce((prev, curr) => {
          return prev.add(curr.weld_time);
        }, moment.duration(first.weld_time))._data,

        // sum of weld inches with more than 1 min weld time
        weld_inches: children
          .filter((item) => {
            return (
              Number(moment.duration({ ...item.weld_time }).asMinutes()) >=
              Number(moment.duration({ minutes: 1 }).asMinutes())
            );
          })
          .reduce((prev, curr) => {
            return prev + curr.weld_inches;
          }, 0)
          .toFixed(1),
      };
    })
    .value();

  return (
    <>
      <Tabs
        activeKey={activeKey}
        size={"small"}
        type="card"
        onTabClick={(key) => setActiveKey(key)}
        tabBarExtraContent={
          <Dropdown
            overlay={
              <Menu>
                {filterData.map((item) => (
                  <Menu.Item
                    key={item.key}
                    onClick={() => {
                      setDropdownKey(item.key);
                    }}
                  >
                    {item.title}
                  </Menu.Item>
                ))}
              </Menu>
            }
          >
            <Button size={"small"}>
              {filterData.find(({ key }) => key === dropdownKey).title}
              <CaretDownOutlined />
            </Button>
          </Dropdown>
        }
      >
        <TabPane tab={"Statistics"} key="key1" style={{}}>
          <Row gutter={16}>
            <Col span={8}>
              <Card title="" bordered={false}>
                <Statistic
                  title={"Error"}
                  value={statsData.error.length}
                  valueStyle={{ color: "red" }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card title="" bordered={false}>
                <Statistic
                  title={"Warning"}
                  value={statsData.warning.length}
                  valueStyle={{ color: "orange" }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card title="" bordered={false}>
                <Statistic
                  title={"Crash"}
                  value={statsData.crash.length}
                  valueStyle={{ color: "red" }}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card title="" bordered={false} type="inner" size="small">
                <Area
                  {...{
                    ...config,
                    seriesField: "type",
                    data: trendsData,
                    height: 100,
                    width: 120,
                    tooltip: true,
                    showMarkers: true,
                    color: ["#fa8c16", "#f5222d", "#fa541c"],
                    line: {
                      size: 1,
                    },
                    xAxis: {
                      label: null,
                    },
                    yAxis: {
                      label: null,
                      grid: {
                        line: {
                          style: null,
                        },
                      },
                    },
                  }}
                />
              </Card>
            </Col>
          </Row>
        </TabPane>
        <TabPane tab={"Alarms List"} key="key2" style={{ overflowY: "scroll" }}>
          <Space align="center" style={{ marginBottom: 16 }}>
            <Switch checked={showCleared} onChange={setShowCleared} />
            Show cleared alarms
          </Space>
          <Table
            className="AlarmsList_Table"
            size={"small"}
            columns={columns}
            dataSource={data.filter((item) => {
              if (showCleared) {
                return moment(item.received_at).isSameOrAfter(
                  filterData
                    .find(({ key }) => key === dropdownKey)
                    .date.format("YYYY-MM-DD")
                );
              }

              return (
                (item.cleared_at !== "" || item.acknowledged_at !== "") &&
                moment(item.received_at).isSameOrAfter(
                  filterData
                    .find(({ key }) => key === dropdownKey)
                    .date.format("YYYY-MM-DD")
                )
              );
            })}
            pagination={pagination}
          />
        </TabPane>
        {!isCustomer && (
          <TabPane
            tab={"Weld History"}
            key="key3"
            style={{ height: 417, overflowY: "scroll", overflowX: "scroll" }}
          >
            <Table
              className="WeldHistoryList_Table"
              size={"small"}
              columns={weldRunColumns}
              dataSource={groupedWeldRun.filter((item) =>
                moment(item.arc_on_utc).isSameOrAfter(
                  filterData
                    .find(({ key }) => key === dropdownKey)
                    .date.format("YYYY-MM-DD")
                )
              )}
              expandable={{
                rowExpandable: (record) => !!record.children,
              }}
              pagination={{ ...pagination, pageSize: 50 }}
            />
          </TabPane>
        )}
      </Tabs>
    </>
  );
}

export default RobotAlarmStatistics;
